<!--Copy right - © 2024 tecera gmbh-->
<script>
    import {
        is_authenticated,
        check_if_authenticated,
        rerout_user,
        user_settings,
        user_role
    } from "../scripts/auth.js";
    import { onMount, onDestroy } from "svelte";
    import { dragHandleZone } from "svelte-dnd-action";
    import {
        all_compound_water_meters,
        selected_device,
        map_center,
        is_modal_open,
        modal_mode,
        water_meter_alarms_triggered_new,
        water_meter_alarms_triggered_old,
        current_device_data,
        last_meta_data,
        free_water_meters,
    } from "../stores.js";
    import NavBar from "../components/NavBar.svelte";
    import Footer from "../components/Footer.svelte";
    import MessgroesseButton from "../components/buttons/MessgroesseButton.svelte";
    import SwitchButton from "../components/buttons/SwitchButton.svelte";
    import SignalDisplay from "../components/SignalDisplay.svelte";
    import StreetMap from "../components/StreetMap.svelte";
    import WaterMeterGraph from "../components/WaterMeterGraph.svelte";
    import NeuesGeraetButton from "../components/buttons/NeuesGeraetButton.svelte";
    import CompoundWaterMeterModal from "../components/modals/CompoundWaterMeterModal.svelte";
    import WaterMeterBoxModal from "../components/modals/WaterMeterBoxModal.svelte";
    import DeviceButton from "../components/buttons/DeviceButton.svelte";
    import Meldungen from "../components/Meldungen.svelte";
    import MeldungenEntry from "../components/MeldungenEntry.svelte";
    import DeviceSearchField from "../components/DeviceSearchField.svelte";
    import DataTabelleNew from "../components/DataTabelleNew.svelte";
    import GrenzwerteDeviceButton from "../components/buttons/GrenzwerteDeviceButton.svelte";
    import AlarmAuswahl from "../components/AlarmAuswahl.svelte";
    import DatePicker from "../components/DatePicker.svelte";
    import SegmentControl2 from "../components/SegmentControl2.svelte";
    import DownloadButton from "../components/buttons/DownloadButton.svelte";
    import AlarmKreisModal from "../components/modals/AlarmKreisModal.svelte";

    import { fade,scale } from "svelte/transition";
    import {
        get_free_water_meters,
        get_alarms_current_water_meter,
        get_alarms_old_water_meter,
        get_compound_water_meters,
        get_compound_water_meter_data,
        create_extended_channel_list,
        change_user_setting,
    } from "../scripts/data.js";
    import {
        find_main_meter,
        sort_devices_by_order,
    } from "../scripts/utils.js";
    import {
        ALARM_REFRESH_INTERVALL_IN_SECONDS,
        DATA_REFRESH_INTERVALL_IN_SECONDS,
        ENABLE_LIVE_RELOADING,
    } from "../config.js";
    import CsvDownloadModal from "../components/modals/CsvDownloadModal.svelte";

    let show_loaders = false;
    let show_old_alarms = false;
    let headline = "Trinkbrunnen";
    let device_search_input = "";
    let init_alarm_fetch = true;
    let alarm_fetch_interval, data_fetch_interval;
    let show_data_table = false;
    let is_mounted = false;
    let chart_data;
    let is_alarmierungskreis_modal_open = false;
    let is_csv_download_modal_open = false;

    const MODALS = {
        COMPOUND_WATER_METER: "compound_water_meter",
        WATER_METER_BOX: "water_meter_box",
        ALARM_KREIS: "alarm_kreis",
        CSV_DOWNLOAD: "csv_download",
        NONE: "none",
    };

    let open_modal = MODALS.NONE;

    var end_date = moment(); // Now
    var start_date = moment().subtract(2, "weeks"); // Two weeks ago
    let data_promise;

    let chosen_segment = "Trinkbrunnen";
    let is_sorting = false;

    onMount(async () => {
        is_mounted = true;
        $is_modal_open = false;
        $selected_device = null; // need to set this to null incase user comes from different tab
        await check_if_authenticated(); // waiting for auth before proceeding
        data_promise = new Promise((resolve) => {
            setTimeout(() => {
                resolve("Fake data for loader");
            }, 500);
        });
        if (
            !$user_role["view_drinking_fountains"] ||
            $user_role["view_drinking_fountains"] != 1
        ) {
            rerout_user();
            return;
        }
        water_meter_order = $user_settings["compound_water_meter_order"] || [];

        fetch_water_meter_configs();
        get_alarms();
        get_free_water_meters(); // can be used as a promise
        if (ENABLE_LIVE_RELOADING) {
            alarm_fetch_interval = setInterval(
                get_alarms,
                ALARM_REFRESH_INTERVALL_IN_SECONDS * 1000,
            );
            data_fetch_interval = setInterval(
                refresh_water_meter_data,
                DATA_REFRESH_INTERVALL_IN_SECONDS * 1000,
            );
        }
    });

    async function fetch_water_meter_configs() {
        show_loaders = true;
        await get_compound_water_meters();
        show_loaders = false;
        $all_compound_water_meters = sort_devices_by_order(
            $all_compound_water_meters,
            water_meter_order,
        );
        $selected_device = $all_compound_water_meters[0];
    }

    async function get_alarms() {
        await get_alarms_current_water_meter();
        init_alarm_fetch = false;
        await get_alarms_old_water_meter();
    }

    async function refresh_water_meter_data() {
        if (
            moment().diff(end_date) / 1000 <
            DATA_REFRESH_INTERVALL_IN_SECONDS * 4
        ) {
            end_date = moment();
        }
        if (
            $selected_device &&
            !$selected_device.box_id &&
            $all_compound_water_meters.some(
                (meter) => meter.id === $selected_device.id,
            )
        ) {
            get_compound_water_meter_data(
                $selected_device.id,
                start_date,
                end_date,
            );
        }
    }

    onDestroy(() => {
        clearInterval(alarm_fetch_interval);
        clearInterval(data_fetch_interval);
        $all_compound_water_meters = [];
        $water_meter_alarms_triggered_new = [];
        $water_meter_alarms_triggered_old = [];
        $free_water_meters = [];
        $selected_device = null;
        $current_device_data = [];
        $last_meta_data = null;
    });

    let is_disabled = false;
    // reactive centering of the map on the selected device
    $: {
        if (
            $selected_device &&
            $all_compound_water_meters.some(
                (meter) => meter.id === $selected_device.id,
            ) &&
            !$selected_device.box_id
        ) {
            select_device_compound();
        }
    }

    function select_device_compound() {
        if (is_sorting) {
            return;
        }

        reset_data();
        create_extended_channel_list({ multiple_devices: true });
        toggle_disable(false);
        let main_meter = find_main_meter($selected_device);
        map_center.set([main_meter.latitude, main_meter.longitude]);
        data_promise = get_compound_water_meter_data(
            $selected_device.id,
            start_date,
            end_date,
        );
        force_segment("Trinkbrunnen");
    }

    function reset_data() {
        $current_device_data = [];
        $last_meta_data = null;
    }
    function toggle_table_view(bool) {
        show_data_table = bool;
    }
    function toggle_disable(bool) {
        is_disabled = bool;
    }
    function force_segment(segment) {
        if (chosen_segment == "Grenzwerte") {
            chosen_segment = "Grenzwerte";
        } else {
            chosen_segment = segment;
        }
    }

    $: {
        if (chosen_segment) {
            is_sorting = false;
        }
    }

    $: {
        if (!is_sorting && $selected_device && start_date && end_date) {
            refresh_water_meter_data();
        }
    }

    // Sorting and Ordering of Devices

    let water_meter_order = [];

    function search_water_meter(device_config, device_search_input) {
        let cleaned_input = device_search_input.toLowerCase().trim();
        return (
            (device_config.name.toLowerCase().includes(cleaned_input) &&
                device_config.watermeterconfigs.length > 0) ||
            (device_config.watermeterconfigs.length > 0 &&
                device_config.watermeterconfigs[0].alt_id
                    .toLowerCase()
                    .includes(cleaned_input))
        );
    }

    function handle_consider_compound_meters(event) {
        filtered_compound_meters = event.detail.items;
    }

    let are_water_meters_changed = false;
    function handle_finalize_compound_meters(event) {
        filtered_compound_meters = event.detail.items;
        are_water_meters_changed = true;
        buffer_water_meter_order();
    }

    let new_water_meter_order = [];
    function buffer_water_meter_order() {
        new_water_meter_order = filtered_compound_meters.map(
            (meter) => meter.id,
        );
    }

    let filtered_compound_meters = [];

    $: {
        filtered_compound_meters = sort_devices_by_order(
            $all_compound_water_meters.filter(
                (meter) =>
                    search_water_meter(meter, device_search_input) ||
                    !meter.name,
            ),
            water_meter_order,
        );
    }

    // save the new order for devices in the database and locally
    function save_device_order() {
        if (are_water_meters_changed) {
            water_meter_order = [...new_water_meter_order];
            change_user_setting(
                "compound_water_meter_order",
                new_water_meter_order,
            );
            $user_settings["compound_water_meter_order"] =
                new_water_meter_order;
            are_water_meters_changed = false;
        }
    }
 $:are_there_alarms = $water_meter_alarms_triggered_new.length > 0
    
// let are_there_alarms=true;
// setInterval(() => {
//     are_there_alarms = !are_there_alarms;
// }, 5000);
let is_measurement_button_disabled = false;
let is_timespan_button_disabled = false;
$: is_measurement_button_disabled = (($user_role && $user_role["filter_measurands"] && $user_role["filter_measurands"] != 1) ? true : false);
$: is_timespan_button_disabled = (($user_role && $user_role["filter_timespan"] && $user_role["filter_timespan"] != 1) ? true : false);

</script>

<svelte:head>
    <title>myBach | Trinkbrunnen</title>
</svelte:head>

{#if $is_authenticated && $user_role["view_drinking_fountains"] == 1}
    <div class="container-fluid">
        <div class="row">
            <!-- Main Content -->
            <main class="col-12 ml-sm-auto px-md-4">
                <NavBar {headline} />
                <div class="row">
                    <!-- Linke Seite -->
                    <div
                        class="{are_there_alarms ? "col-12 col-xl-7 col-lg-6" : "col-12 col-xl-9 col-lg-8"} content_main"
                        style="display:flex;flex-direction:column;justify-content:flex-start;"
                                transition:scale|local={{ duration: 200 }}

                    >
                        <div class="row">
                            <div
                                class="col-12"
                                style="display:flex;flex-direction:column;justify-content:flex-start;"
                            >
                                <div class="row" style="flex: 0 1 5%;">
                                    <!-- Buttons -->
                                    <div
                                        class="col-12 col-lg-9 col-md-8 pl-3 pt-3 pr-0"
                                        style="align-items: flex-start;justify-content: flex-start;display:flex;flex: 1 1 60%;"
                                    >
                                        <div
                                            class="d-md-flex d-inline justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2"
                                        >
                                            <div class="btn-toolbar pb-1">
                                                {#if $user_role["create_device"] == 1}
                                                <NeuesGeraetButton
                                                    label={"Neues Gerät"}
                                                    func={() => {
                                                        $modal_mode = "create";
                                                        $is_modal_open = true;
                                                        open_modal =
                                                            MODALS.COMPOUND_WATER_METER;
                                                    }}
                                                />
                                                {/if}
                                                <DatePicker
                                                    bind:start_date
                                                    bind:end_date
                                                    bind:is_disabled={
                                                        is_timespan_button_disabled
                                                    }
                                                />
                                                <MessgroesseButton
                                                    multiple_devices={true}
                                                    bind:chart_data
                                                    bind:is_disabled={is_measurement_button_disabled}
                                                />
                                                <SwitchButton
                                                    bind:toggle_state={
                                                        show_data_table
                                                    }
                                                    bind:is_disabled
                                                    label0="Tabelle"
                                                    label1="Diagramm"
                                                    icon_0="fa-table"
                                                    icon_1="fa-chart-line"
                                                />
                                                <DownloadButton
                                                    bind:modal_toggle={
                                                        is_csv_download_modal_open
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Letzter Sendezeitraum und Signalstaerke -->
                                    <div
                                        class="col-3 col-lg-3 col-md-4 p-0 ml-0 pr-2 pt-3"
                                        style="align-items: flex-end;justify-content: flex-end;display:flex;flex: 1 0 150px;min-width:fit-content;"
                                    >
                                        <SignalDisplay />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {#if show_data_table}
                            <div
                                class="row"
                                style="flex: 1 1 70%;overflow:hidden;"
                                in:fade|local={{ duration: 200 }}
                            >
                                <DataTabelleNew
                                    {is_mounted}
                                    bind:chart_data
                                    multiple_devices={true}
                                />
                            </div>
                        {:else}
                            <!-- Diagramm -->
                            <div class="row pt-4" style="flex: 1 1 70%;overflow:hidden;">
                                {#await data_promise}
                                    <div class="col-12 full">
                                        <div
                                            id="loader_devices"
                                            class="loader"
                                        />
                                    </div>
                                {:then}
                                    {#if $current_device_data && $current_device_data[0] && $current_device_data[0].data && $current_device_data[0].data.length > 0}
                                        <div class="col-12 full">
                                            <WaterMeterGraph />
                                        </div>
                                    {:else}
                                        <div
                                            class="col-12 full center-flex-box"
                                        >
                                            <p class="h1 p-kachel-2">
                                                Keine Daten im angegebenen
                                                Zeitraum verfügbar
                                            </p>
                                        </div>
                                    {/if}
                                {/await}
                            </div>
                        {/if}
                    </div>

                    <!-- Rechte Seite -->
                    <div
                        class="col content_main content_main_side"
                        style="display:flex;flex-direction:column;justify-content:flex-start;"
                                transition:scale|local={{ duration: 200 }}
                    >
                        <div class="row pt-4" style="height:50%;flex-wrap:nowrap;">
                            <!-- Meldungen -->
                             {#if are_there_alarms && $user_role["view_alarms_triggered"] == 1}
                            <div class="col-5" style="height:100%;"
                                in:scale|local={{ duration: 200 }}
                            >
                                <Meldungen bind:show_old_alarms>
                                    {#if init_alarm_fetch}
                                        <div
                                            id="loader_devices"
                                            class="loader"
                                        />
                                    {:else if show_old_alarms}
                                        {#each $water_meter_alarms_triggered_old as alarm_triggered (alarm_triggered.id)}
                                            <MeldungenEntry
                                                {alarm_triggered}
                                                {show_old_alarms}
                                                type="water_meter"
                                                alarm_list={water_meter_alarms_triggered_old}
                                            />
                                        {/each}
                                    {:else}
                                        {#each $water_meter_alarms_triggered_new as alarm_triggered (alarm_triggered.id)}
                                            <MeldungenEntry
                                                {alarm_triggered}
                                                {show_old_alarms}
                                                type="water_meter"
                                                alarm_list={water_meter_alarms_triggered_new}
                                            />
                                        {/each}
                                    {/if}
                                </Meldungen>
                            </div>
                            {/if}
                            <!-- water meter Menü -->
                            <div class="col" style="height:100%;" transition:scale|local={{ duration: 200 }}>
                                <div
                                    class="display_box side-menu side-menu-scroll"
                                    style="position:relative;"
                                >
                                {#if $user_role["view_alarms_settings"] == 1}
                                    <SegmentControl2
                                        bind:chosen_segment
                                        segments={[
                                            "Trinkbrunnen",
                                            "Grenzwerte",
                                        ]}
                                        classes="mb-3"
                                    />
                                {/if}

                                    {#if chosen_segment == "Trinkbrunnen"}
                                        <!-- Suchfeld -->
                                        <DeviceSearchField
                                            bind:device_search_input
                                            classes="mb-2"
                                            bind:is_sorting
                                            is_sorting_enabled={true}
                                            on_save={save_device_order}
                                        />
                                        {#if show_loaders}
                                            <div
                                                id="loader_menu"
                                                class="scroll_window"
                                            >
                                                <div
                                                    id="loader_devices"
                                                    class="loader"
                                                />
                                            </div>
                                        {:else}
                                            <div
                                                id="water_meter_drag_drop_menu"
                                                class="scroll_window list-group"
                                                style="border-radius:0.25rem;"
                                                use:dragHandleZone={{
                                                    items: filtered_compound_meters,
                                                    dropTargetStyle: {
                                                        outline:
                                                            "2px solid white",
                                                    },
                                                }}
                                                on:consider={handle_consider_compound_meters}
                                                on:finalize={handle_finalize_compound_meters}
                                            >
                                                {#each filtered_compound_meters as device_config (device_config.id)}
                                                    <DeviceButton
                                                        {device_config}
                                                        multiple_devices={true}
                                                        opens_modal={MODALS.COMPOUND_WATER_METER}
                                                        bind:open_modal
                                                        bind:is_sorting
                                                    />
                                                {/each}
                                            </div>
                                        {/if}
                                    {:else if chosen_segment == "Grenzwerte"}

                                        <GrenzwerteDeviceButton
                                            device_list={all_compound_water_meters}
                                            bind:is_disabled
                                        />
                                        {#if $user_role["view_alarms_recipients"] == 1}
                                        <div class="input-group mb-1 mt-1">
                                            <button
                                                id="show_alarmierungskreis"
                                                class="btn btn-primary btn-block {is_disabled
                                                    ? 'disabled'
                                                    : ''}"
                                                on:click={() => {
                                                    if (is_disabled) {
                                                        return;
                                                    }
                                                    is_alarmierungskreis_modal_open = true;
                                                }}
                                            >
                                                Alarmierungskreis
                                            </button>
                                        </div>
                                        {/if}
                                        <div
                                            id="hymoMenu"
                                            class="scroll_window mt-1"
                                            style="background-color:var(--side-menu-color);padding-left:10px;border-radius:0.2rem;font-size:smaller;"
                                        >
                                            <AlarmAuswahl
                                                many={true}
                                                bind:is_disabled
                                            />
                                        </div>
                                    {/if}
                                </div>
                            </div>
                        </div>
                        <div class="row pt-4" style="height:50%;">
                            <!-- Map -->
                            <div
                                class="col-12"
                                style="height:100%;min-height:200px;"
                                transition:scale|local={{ duration: 200 }}
                                
                            >
                                <StreetMap
                                    devices={all_compound_water_meters}
                                    alarms_triggered={water_meter_alarms_triggered_new}
                                    multiple_devices={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    </div>

    <CompoundWaterMeterModal modals={MODALS} bind:open_modal />
    <WaterMeterBoxModal modals={MODALS} bind:open_modal />
    <CsvDownloadModal bind:is_open={is_csv_download_modal_open} />

    <AlarmKreisModal
        bind:is_open={is_alarmierungskreis_modal_open}
        multiple_devices={true}
    />
    <Footer />
{/if}
